/**
 * The order history, read as a sequence rather than as a list of moments.
 *
 * Two changes from what was there before, and both are about the same question. A
 * dispatcher looking at a history is almost always asking "how long did that take?" — how
 * long the driver sat on the offer before refusing it, how long the car stood at the pickup
 * before the passenger came out. Minute-precision timestamps cannot answer it, and a column
 * of absolute times makes the reader do the arithmetic.
 *
 * So the clock carries seconds, and the gap since the previous event is its own column. An
 * offer and its refusal are two events, which means the gap between them is exactly how long
 * the driver took to say no.
 */

/** How the gap is written: seconds under a minute, minutes and seconds above it. */
export function gapLabel(previousIso: string | null | undefined, iso: string): string {
  if (previousIso == null) return '';

  const seconds = Math.floor((Date.parse(iso) - Date.parse(previousIso)) / 1000);

  // Clamped rather than shown negative. Events arrive from several writers and a clock
  // that disagrees by half a second is not something to report to a dispatcher.
  if (!Number.isFinite(seconds) || seconds <= 0) return '+0 s';

  if (seconds < 60) return `+${seconds} s`;

  return `+${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** The clock, with seconds, in the firm's timezone rather than the browser's. */
export function clockWithSeconds(
  iso: string,
  formats: { timeWithSeconds: (iso: string) => string },
): string {
  return formats.timeWithSeconds(iso);
}

// --- Why a line was written ------------------------------------------------------

export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The driver app's reasons for turning an offer down. `no_response` is not a button there —
 * it is what the app sends on the driver's behalf when the countdown runs out.
 */
export const DECLINE_REASONS = [
  'too_far',
  'finishing_shift',
  'break',
  'vehicle_problem',
  'other',
  'no_response',
];

/**
 * The codes that tell a reader something the line beside them does not: why an order went
 * back to looking for a car, or who settled it. The rest — `order.created`,
 * `dispatch.assigned`, `driver.reported`, a cancellation's own code — repeat what the state
 * and who did it already say.
 */
const SAID: Record<string, string> = {
  'driver.declined': 'declined',
  'dispatch.offer_expired': 'offerExpired',
  'dispatch.reassigned': 'reassigned',
  'dispatch.claimed': 'claimed',
  'dispatch.no_cars_available': 'noCars',
  'dispatch.needs_human': 'needsHuman',
  'dispatch.prebooking_at_risk': 'atRisk',
  'dispatch.taken_by_hand': 'takenByHand',
  'dispatch.automatic_resumed': 'automaticResumed',
  'order.street_ride': 'streetRide',
};

/** A machine's code — one of the driver app's, or dotted and lower case — not a person's words. */
export function isCode(reason: string): boolean {
  return DECLINE_REASONS.includes(reason) || /^[a-z_]+(\.[a-z_]+)+$/.test(reason);
}

/** A driver's reason for turning an offer down, in words; null when it is not one of the app's. */
export function declineWords(t: Translate, reason: string): string | null {
  return DECLINE_REASONS.includes(reason) ? t(`order.declineReason.${reason}`) : null;
}

/** The starter reasons a passenger may give for cancelling, with words in the shared bundle. */
export const STARTER_CANCELLATION_REASONS = [
  'changed_mind',
  'waited_too_long',
  'found_other',
  'wrong_address',
  'other',
];

/** How the API records the reason a passenger chose: `rider.<code>` (#464). */
const RIDER_REASON = 'rider.';

/**
 * Whether a recorded reason is one a passenger chose, which the firm's list says best: a
 * reason it added has no other words, and a starter it renamed has the firm's.
 */
export function isRiderReason(reason: string | null | undefined): boolean {
  return reason?.startsWith(RIDER_REASON) ?? false;
}

/**
 * A cancellation reason's name: the starter's words in the reader's language until the firm
 * renames it, then the firm's own.
 */
export function cancellationReasonName(
  t: Translate,
  code: string,
  displayName: string,
  namedByFirm: boolean,
): string {
  if (namedByFirm || !STARTER_CANCELLATION_REASONS.includes(code)) return displayName;

  return t(`cancellationReasons.${code}`, { defaultValue: displayName });
}

/**
 * The reason a passenger chose when cancelling, in words — or null when `reason` is not one.
 *
 * A starter is said in the reader's language. A reason the firm added itself has a code
 * nobody can read (`firm_…`), so it is said by the firm's name for it when the page has the
 * firm's list — the order card and the replay fetch it for any order a passenger cancelled
 * with a reason (`cancellationReasons.ts`) — and otherwise as "a reason of the firm's own". A
 * starter the firm renamed is said in the firm's words from the same list, and in ours when
 * the list could not be read. An app built before reasons existed records
 * `order.cancelled_by_rider`, which this leaves alone: the line already says who cancelled.
 */
export function riderCancellationWords(
  t: Translate,
  reason: string,
  firmNames?: Readonly<Record<string, string>>,
): string | null {
  if (!reason.startsWith(RIDER_REASON)) return null;

  const code = reason.slice(RIDER_REASON.length);

  if (code === '') return null;

  if (firmNames !== undefined && Object.hasOwn(firmNames, code)) {
    return t('order.reason.cancelledByRider', { why: firmNames[code] });
  }

  if (STARTER_CANCELLATION_REASONS.includes(code)) {
    return t('order.reason.cancelledByRider', { why: t(`cancellationReasons.${code}`) });
  }

  return t('order.reason.cancelledByRiderOwnReason');
}

/**
 * Why a line of an order's history was written, in words — or null when the line already
 * says it.
 *
 * A code is never shown as it is: a dispatcher reading `dispatch.offer_expired` has to know
 * the code to know the offer ran out. One with something to add is said in words; one that
 * only repeats the line is left out, and so is one nobody has words for yet. Anything that is
 * not a code was typed by a person, and is shown as they typed it.
 */
export function historyReason(
  t: Translate,
  reason: string | null | undefined,
  firmReasons?: Readonly<Record<string, string>>,
): string | null {
  if (!reason) return null;

  const why = declineWords(t, reason);

  if (why !== null) return t('order.reason.declinedBecause', { why });

  const cancelled = riderCancellationWords(t, reason, firmReasons);

  if (cancelled !== null) return cancelled;

  if (Object.hasOwn(SAID, reason)) return t(`order.reason.${SAID[reason]}`);

  return isCode(reason) ? null : reason;
}
